import React, { useEffect, useState } from 'react';
import SkillCard from './SkillCard';
import {useCharacter} from "./CharacterProvider";
import { isLockedSkill } from './utils';

// Skillsets the player actually chooses (Fighter, Rogue, Arcane, Spirit, Nature,
// Thaumaturgy). A 0-cost skill in one of these (e.g. Last Rites) belongs under
// its skillset, not in the catch-all "Static" group.
const MAIN_SKILLSET_IDS = [1, 2, 3, 4, 5, 6];

// Transform abilities are skillset 8 (see AvailableSkills.js).
const TRANSFORM_SKILLSET_ID = 8;

const TIER_LABELS = ['Static', 'Basic', 'Advanced', 'Expert', 'Master', 'Grandmaster', 'Mythic'];

// Group skills by tier and return entries sorted by tier (ascending).
const groupByTier = (skills) => {
    const byTier = {};
    skills.forEach((s) => {
        const t = s.tier ?? 0;
        (byTier[t] = byTier[t] || []).push(s);
    });
    return Object.entries(byTier).sort(([a], [b]) => Number(a) - Number(b));
};

const SkillList = ({
                       availableSkills,
                       skillsetMap,
                       getSkillCost,
                       getActionCost,
                       onIncrement,
                       onDecrement,
                       onRemove,
                   }) => {
    const {currentCharacter, setCurrentCharacter} = useCharacter();

    // Which skill's description is unfolded (one at a time).
    const [expandedSkillId, setExpandedSkillId] = useState(null);

    const skillsArray = Array.isArray(currentCharacter?.skills) ? currentCharacter.skills : [];

    // Collapse the open description whenever a skill is added or removed.
    const skillIdSignature = skillsArray.map(s => s.skillId).join(',');
    useEffect(() => {
        setExpandedSkillId(null);
    }, [skillIdSignature]);

    if (!currentCharacter || !currentCharacter.skills) return <p>No skills assigned.</p>;
    if (skillsArray.length === 0) return <p>No skills assigned.</p>;

    const toggleDescription = (skillId) =>
        setExpandedSkillId(prev => (prev === skillId ? null : skillId));

    const isSkillPrimary = (skill) => {
        return skill && skill.skillset && (Number(skill.skillset) === Number(currentCharacter.skillsetPrimary) || skill.secondaryBaseCost == null);
    };

    const isSkillSecondary = (skill) => {
        return skill && skill.skillset && Number(skill.skillset) === Number(currentCharacter.skillsetSecondary);
    };

    // Group the skills by skillset or source.
    const groupedSkills = skillsArray.reduce((acc, skill) => {
        const baseSkill = availableSkills.find(s => s.skillId === skill.skillId);
        // Determine the group key
        let groupKey;
        if (baseSkill && Number(baseSkill.skillset) === TRANSFORM_SKILLSET_ID) {
            groupKey = 'transform';
        } else if (
            baseSkill &&
            baseSkill.primaryBaseCost === 0 &&
            !MAIN_SKILLSET_IDS.includes(Number(baseSkill.skillset)) &&
            !skillsetMap?.[String(baseSkill.skillset)]
        ) {
            // 0-cost utility/racial skills with no named skillset → "Static".
            groupKey = 'static';
        } else if (baseSkill && baseSkill.skillset !== undefined) {
            groupKey = String(baseSkill.skillset);
        } else {
            groupKey = 'unknown';
        }

        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }

        // Resolve the displayed tier rate into locals — never mutate `skill`,
        // which is a live object from currentCharacter.skills (React state).
        let baseCost, incrementCost;
        if (isSkillPrimary(baseSkill)) {
            baseCost = baseSkill.primaryBaseCost;
            incrementCost = baseSkill.primaryIncrementCost;
        } else if (isSkillSecondary(baseSkill)) {
            baseCost = baseSkill.secondaryBaseCost;
            incrementCost = baseSkill.secondaryIncrementCost;
        } else if (baseSkill) {
            baseCost = baseSkill.tertiaryBaseCost;
            incrementCost = baseSkill.tertiaryIncrementCost;
        }
        acc[groupKey].push({ ...baseSkill, ...skill, baseCost, incrementCost });
        return acc;
    }, {});

    const localSkillsetMap = {
        ...skillsetMap,
        static: 'Static',
    };

    const freeSkillsKey = Object.keys(skillsetMap || {}).find(k => skillsetMap[k] === 'Free Skills');
    const bottomRank = (key) => (key === freeSkillsKey ? 2 : key === 'static' ? 1 : 0);

    // Sort the group keys: pinned-to-bottom groups first by rank, then alphabetically.
    const sortedGroupKeys = Object.keys(groupedSkills).sort((a, b) => {
        const ra = bottomRank(a), rb = bottomRank(b);
        if (ra !== rb) return ra - rb;
        const labelA = a === 'transform' ? 'Transform Abilities' : (localSkillsetMap?.[a] || '').toUpperCase();
        const labelB = b === 'transform' ? 'Transform Abilities' : (localSkillsetMap?.[b] || '').toUpperCase();
        return labelA.localeCompare(labelB);
    });

    return (
        <div className="skills-list">
            {sortedGroupKeys.map((groupKey, index) => (
                <div key={`group-${index}`} className="floating-label skills-list-form-group">
                    <div className="skill-group">
                        <div className="group-header">
                            {/*<h4>{groupKey === 'transform' ? 'Transform Abilities' : skillsetMap?.[groupKey] || groupKey}</h4>*/}
                            {groupKey === 'transform' ? 'Transform Abilities' : localSkillsetMap?.[groupKey] || groupKey}
                        </div>
                        <div className="group-skills">
                            {groupKey === 'transform'
                                ? groupByTier(groupedSkills[groupKey]).map(([tier, skills]) => (
                                    <React.Fragment key={`tier-${tier}`}>
                                        <div className="transform-tier-header">
                                            {TIER_LABELS[Number(tier)] || `Tier ${tier}`}
                                        </div>
                                        {skills
                                            .sort((s1, s2) => (s1.name || '').localeCompare(s2.name || ''))
                                            .map((skill) => (
                                                <SkillCard
                                                    key={skill.skillId || skill.name}
                                                    skill={skill}
                                                    spCost={getSkillCost(skill)}
                                                    getActionCost={getActionCost}
                                                    locked={isLockedSkill(skill)}
                                                    expanded={expandedSkillId === skill.skillId}
                                                    onToggleDescription={toggleDescription}
                                                    onIncrement={onIncrement}
                                                    onDecrement={onDecrement}
                                                    onRemove={onRemove}
                                                    description={skill.description}
                                                />
                                            ))}
                                    </React.Fragment>
                                ))
                                : groupedSkills[groupKey]
                                    .sort((s1, s2) => (s1.name || '').localeCompare(s2.name || ''))
                                    .map((skill) => (
                                        <SkillCard
                                            key={skill.skillId || skill.name}
                                            skill={skill}
                                            spCost={getSkillCost(skill)}
                                            getActionCost={getActionCost}
                                            locked={isLockedSkill(skill)}
                                            expanded={expandedSkillId === skill.skillId}
                                            onToggleDescription={toggleDescription}
                                            onIncrement={onIncrement}
                                            onDecrement={onDecrement}
                                            onRemove={onRemove}
                                            description={skill.description}
                                        />
                                    ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default SkillList;
